/**
 * Phase 93 (Plan 93-01, Task 1) — ModulesProvider + useModules().
 *
 * Structural clone of `authContext.tsx`'s `AuthProvider`/`useAuth` shape,
 * mounted INSIDE `AuthProvider` (D-1 / research Pitfall 3) so its
 * `GET /api/modules` fetch — which is `authenticate`-gated — only fires once
 * `isAuthenticated` is known, never in parallel with `/auth/me`.
 *
 * This gate is presentation-only: the enforcement boundary is the backend
 * never registering a disabled module's Fastify plugins (a genuine 404) —
 * see `docs/private/MODULES.md`. A tampered/forged `hasModule()` at most reveals a
 * link to a URL that then still 404s server-side (T-93-01).
 *
 * Fail-open by design (T-93-02): while the query is loading, on error, and
 * when no `ModulesProvider` is mounted at all, every module is treated as
 * enabled. This avoids a nav/route flash on the common all-modules-enabled
 * case and means a `/api/modules` outage degrades to today's UI rather than
 * a blank nav (T-93-02 mitigation).
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from './authContext';
import { useEnabledModules } from '../hooks/useModules';
import { MODULE_IDS, type ModuleId } from './api/modules';

export interface ModulesContextValue {
  hasModule(id: ModuleId): boolean;
  isLoading: boolean;
  enabled: readonly ModuleId[];
}

const ModulesContext = createContext<ModulesContextValue | null>(null);

/** Matches `authContext.tsx:133`'s dev-stub check exactly. */
function isDevStub(): boolean {
  return !!(import.meta.env.DEV && import.meta.env.VITE_DEV_USER);
}

export function ModulesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const devStub = isDevStub();

  const { data, isLoading } = useEnabledModules(isAuthenticated && !devStub);

  const value = useMemo<ModulesContextValue>(() => {
    // devStub, loading, or errored (no `data`) all resolve to "every module
    // enabled" — the fail-open default (D-1 / T-93-02).
    const enabled: readonly ModuleId[] = devStub || !data ? MODULE_IDS : data.enabled;
    const enabledSet = new Set(enabled);
    return {
      hasModule: (id: ModuleId) => enabledSet.has(id),
      isLoading: devStub ? false : isLoading,
      enabled,
    };
  }, [devStub, data, isLoading]);

  return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>;
}

/**
 * Returns the current modules context, or an all-enabled fallback when no
 * `ModulesProvider` is in the tree (fail-open; keeps existing Sidebar specs
 * and any future component spec working without provider ceremony, and
 * matches the loading/error default).
 */
export function useModules(): ModulesContextValue {
  const context = useContext(ModulesContext);
  if (context) return context;
  return {
    hasModule: () => true,
    isLoading: false,
    enabled: MODULE_IDS,
  };
}
